import { AxiosHttpClient } from "./AxiosHttpService";
import { ExpertVerification } from "../models/user";

export type ExpertReviewAction = "VERIFY" | "REJECT" | "SUSPEND";

/**
 * Service pour la vérification des profils experts.
 * - côté expert : consulter son statut, soumettre des justificatifs.
 * - côté admin : consulter le dossier d'un expert et rendre une décision.
 * - téléchargement sécurisé des justificatifs (propriétaire ou admin).
 */
export class ExpertVerificationService {
  /** Statut de vérification de l'expert connecté (+ documents). */
  static getMyVerification(): Promise<ExpertVerification> {
    return AxiosHttpClient.get<ExpertVerification>(
      "api/v1/docuveritas/experts/verification/me"
    );
  }

  /** Soumission (ou re-soumission) des justificatifs par l'expert connecté. */
  static submitDocuments(files: File[]): Promise<ExpertVerification> {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    return AxiosHttpClient.post<ExpertVerification>(
      "api/v1/docuveritas/experts/verification/me/documents",
      formData
    );
  }

  /** Dossier de vérification d'un expert (réservé aux administrateurs). */
  static getVerificationForAdmin(expertId: string): Promise<ExpertVerification> {
    return AxiosHttpClient.get<ExpertVerification>(
      `api/v1/docuveritas/admin/experts/${expertId}/verification`
    );
  }

  /** Décision d'un administrateur sur un expert. */
  static review(
    expertId: string,
    action: ExpertReviewAction,
    reason?: string
  ): Promise<ExpertVerification> {
    return AxiosHttpClient.post<ExpertVerification>(
      `api/v1/docuveritas/admin/experts/${expertId}/verification/review`,
      { action, reason: reason ?? null }
    );
  }

  /**
   * Récupère un justificatif via la route sécurisée et renvoie une URL objet
   * exploitable pour prévisualiser ou télécharger le fichier.
   */
  static async getDocumentObjectUrl(documentId: string): Promise<string> {
    const blob = await AxiosHttpClient.getBlob(
      `api/v1/docuveritas/experts/verification/documents/${documentId}`
    );
    return URL.createObjectURL(blob);
  }
}
